import * as React from 'react';
import { Component } from 'react';
import { History, Location } from 'history';
import * as Yup from 'yup';
import Moment from 'moment';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { DateTimePickerField } from 'Src/components/DateTimePickerField/DateTimePickerField';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { UserProfile } from '../../../../models/profile';
import { DATE_FORMAT } from '../../../../consts/date';
import {
  USER_GENDER,
  GENDER_SERVER_TO_CLIENT_MAPPING,
  GENDER_NOT_DEFINED_SERVER_TO_CLIENT_MAPPING
} from '../../../../consts/user';
import { getPhoneAndCode } from '../../../../helpers/phone/phone';
import { setProfile } from 'Src/helpers/service/admin/user';
import './ProfileSummary.scss';
import { uploadImage } from '../../../../helpers/service/image';
import { DEFAULT_LIMIT } from '../../../../consts/table';
import { getPermissions } from 'Src/helpers/service/admin/user';
import { ROLE } from '../../../../consts/user';
import { Loader } from 'Src/components/Loader/Loader';

interface Props {
  user: AppUser;
  history: History;
  location: Location;
  updateProfile: (profile: UserProfile) => void;
  setIsEditMode: (boolean) => void;
  isEditMode: boolean;
}

interface State {
  isPhoneCodeOptionsVisible: boolean;
  isLoading: boolean;
  isStudentRole: boolean;
  isLoadingPhoto: boolean;
}

const profileSummarySchema = Yup.object().shape({
  firstName: Yup.string().required('please enter your name'),
  lastName: Yup.string().required('please enter your surname'),
  birthday: Yup.date()
});

export class ProfileSummary extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      isPhoneCodeOptionsVisible: false,
      isLoading: false,
      isStudentRole: false,
      isLoadingPhoto: false
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true });

    const filter = {
      limit: DEFAULT_LIMIT,
      skip: 0
    };

    const { user } = this.props;
    let isStudentRole = false;

    getPermissions(user, filter).then(permissions => {
      permissions.forEach(permission => {
        if (permission.preset === ROLE.STUDENT) {
          isStudentRole = true;
        }
      });

      this.setState({
        isStudentRole,
        isLoading: false
      });
    });
  }

  onEditClick = (): void => {
    this.props.setIsEditMode(true);
  };

  onCancelClick = (resetForm): void => {
    resetForm();
    this.props.setIsEditMode(false);
  };

  onAvatarChange = (event, setFieldValue) => {
    const avatarFile = event.target.files[0];
    const { user } = this.props;

    if (!avatarFile) {
      return;
    }

    this.setState({ isLoadingPhoto: true });

    uploadImage(user, avatarFile).then(res => {
      const { key } = res;
      const avatarUrl = `${window.apiImg}/images/${key}`;
      setFieldValue('avatar', avatarUrl);

      this.setState({ isLoadingPhoto: false });
    });
  };

  onSubmit = data => {
    const { user, updateProfile } = this.props;

    this.setState({ isLoading: true });

    setProfile(user, data).then(profile => {
      updateProfile(profile);
      this.setState({ isLoading: false });
      this.props.setIsEditMode(false);
    });
  };

  isProfileInfoDisabled = () => {
    const { isStudentRole } = this.state;
    const { isEditMode } = this.props;

    return isStudentRole ? true : !isEditMode;
  };

  getInitials = () => {
    const firstName = propz.get(this.props.user, ['profile', 'firstName'], '');
    const lastName = propz.get(this.props.user, ['profile', 'lastName'], '');

    return `${firstName ? firstName.charAt(0) : ''}${lastName ? lastName.charAt(0) : ''}`.toUpperCase() || 'U';
  };

  renderVerifiedStatus = (isVerified: boolean) => {
    return (
      <span
        className={isVerified ? 'upProfileStatus upProfileStatusSuccess' : 'upProfileStatus upProfileStatusWarning'}
      >
        {isVerified ? 'Verified' : 'Not verified'}
      </span>
    );
  };

  render() {
    const { user, isEditMode } = this.props;
    const { profile } = user;
    const { isLoading, isLoadingPhoto, isStudentRole } = this.state;
    const { avatar, firstName, lastName, gender, birthday, email, phone } = profile;
    const isEmailVerified = propz.get(profile, ['verification', 'status', 'email'], false);
    const isPhoneVerified = propz.get(profile, ['verification', 'status', 'sms'], false);

    if (isLoading) {
      return <Loader />;
    }

    const isAvatarUrlExist = typeof avatar !== 'undefined' && avatar !== '';
    const isGenderExist = typeof gender !== 'undefined' && gender !== null;
    const phoneObj = getPhoneAndCode(phone);
    const { code: phoneCode, phone: phoneBody } = phoneObj;

    const profileSummaryForm = {
      avatar,
      firstName,
      lastName,
      gender: isGenderExist ? gender : '',
      birthday: birthday ? Moment(birthday) : ''
    };

    const isProfileInfoDisabled = this.isProfileInfoDisabled();

    return (
      <Formik
        initialValues={profileSummaryForm}
        validationSchema={profileSummarySchema}
        onSubmit={values => {
          const dataCopy = { ...values };

          if (dataCopy.birthday) {
            dataCopy.birthday = Moment(values.birthday).format('YYYY-MM-DD');
          } else {
            delete dataCopy.birthday;
          }

          if (!dataCopy.gender) {
            delete dataCopy.gender;
          }

          this.onSubmit(dataCopy);
        }}
      >
        {({ values, setFieldValue, errors, touched, resetForm }) => (
          <Form className="upProfileSummaryForm">
            <section className="upProfileCard">
              <header className="upProfileCardHeader">
                <div>
                  <h2 className="upProfileCardTitle">Your details</h2>
                  <p className="upProfileCardSub">
                    {isStudentRole
                      ? 'Student profiles are managed by your school.'
                      : 'Your account details and profile photo.'}
                  </p>
                </div>
                <div className="upProfileSummaryActions">
                  {!isEditMode ? (
                    !isStudentRole && (
                      <button onClick={this.onEditClick} type="button" className="upProfileBtn upProfileBtnSecondary">
                        Edit
                      </button>
                    )
                  ) : (
                    <>
                      <button
                        onClick={() => this.onCancelClick(resetForm)}
                        type="button"
                        className="upProfileBtn upProfileBtnSecondary"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="upProfileBtn upProfileBtnPrimary">
                        Save
                      </button>
                    </>
                  )}
                </div>
              </header>

              <div className="upProfileCardBody upProfileSummaryBody">
                <aside className="upProfilePhotoPane">
                  <div className="upProfilePhotoWrap">
                    {isLoadingPhoto ? (
                      <div className="upProfilePhotoFrame">
                        <Loader />
                      </div>
                    ) : isAvatarUrlExist || values.avatar ? (
                      <img src={values.avatar} className="upProfilePhoto" alt="avatar" />
                    ) : (
                      <div className="upProfilePhotoInitials">{this.getInitials()}</div>
                    )}
                    {isEditMode && !isStudentRole && (
                      <label className="upProfilePhotoButton" htmlFor="profile_summary_file_input">
                        +
                      </label>
                    )}
                    <input
                      type="file"
                      className="eProfileSummaryFileInput"
                      id="profile_summary_file_input"
                      accept=".jpg, .jpeg, .png"
                      onChange={event => this.onAvatarChange(event, setFieldValue)}
                      disabled={!isEditMode || isStudentRole}
                    />
                  </div>
                  <p>Shown on your comments and to other users.</p>
                </aside>

                <div className="upProfileFieldsPane">
                  <div className="upProfileFieldGrid">
                    <div className="upProfileField">
                      <div className="upProfileLabelRow">
                        <label
                          className={
                            touched.firstName && errors.firstName
                              ? 'upProfileLabel upProfileLabelError'
                              : 'upProfileLabel'
                          }
                        >
                          Name
                        </label>
                        <ErrorMessage component="span" className="upProfileErrorText" name="firstName" />
                      </div>
                      <Field
                        type="text"
                        name="firstName"
                        disabled={isProfileInfoDisabled}
                        className={
                          touched.firstName && errors.firstName
                            ? 'eProfileSummaryInput mProfileSummaryInputWithError'
                            : 'eProfileSummaryInput'
                        }
                      />
                    </div>

                    <div className="upProfileField">
                      <div className="upProfileLabelRow">
                        <label
                          className={
                            touched.lastName && errors.lastName
                              ? 'upProfileLabel upProfileLabelError'
                              : 'upProfileLabel'
                          }
                        >
                          Surname
                        </label>
                        <ErrorMessage component="span" className="upProfileErrorText" name="lastName" />
                      </div>
                      <Field
                        type="text"
                        name="lastName"
                        disabled={isProfileInfoDisabled}
                        className={
                          touched.lastName && errors.lastName
                            ? 'eProfileSummaryInput mProfileSummaryInputWithError'
                            : 'eProfileSummaryInput'
                        }
                      />
                    </div>

                    <div className="upProfileField">
                      <label className="upProfileLabel">Gender</label>
                      <Field
                        component="select"
                        name="gender"
                        className="eProfileSummaryInput"
                        disabled={isProfileInfoDisabled}
                      >
                        <option value={USER_GENDER.MALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.MALE}</option>
                        <option value={USER_GENDER.FEMALE}>{GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE}</option>
                        <option value="">{GENDER_NOT_DEFINED_SERVER_TO_CLIENT_MAPPING.NULL}</option>
                      </Field>
                    </div>

                    <div className="upProfileField">
                      <label className="upProfileLabel">Date of birth</label>
                      {isProfileInfoDisabled ? (
                        <input
                          type="text"
                          className="eProfileSummaryInput"
                          value={values.birthday ? Moment(values.birthday).format('D MMM YYYY') : ''}
                          disabled={true}
                        />
                      ) : (
                        <Field name="birthday">
                          {() => {
                            let date;

                            if (Moment(values.birthday, DATE_FORMAT).isValid()) {
                              date = Moment(values.birthday, DATE_FORMAT).toDate();
                            }

                            return (
                              <DateTimePickerField
                                value={date || null}
                                onChange={birthdayDate => {
                                  if (birthdayDate !== null) {
                                    setFieldValue('birthday', birthdayDate);
                                  } else {
                                    setFieldValue('birthday', '');
                                  }
                                }}
                                showTimeSelect={false}
                                disabled={isProfileInfoDisabled}
                                inputClassName="eProfileSummaryInput"
                                placeholderText="DD Mmm YYYY"
                                popperPlacement="bottom-start"
                                dateFormat="d MMM yyyy"
                              />
                            );
                          }}
                        </Field>
                      )}
                    </div>
                  </div>

                  <div className="upProfileDivider" />

                  <div className="upProfileFieldGrid">
                    <div className="upProfileField">
                      <div className="upProfileLabelWithStatus">
                        <label className="upProfileLabel">Email</label>
                        {this.renderVerifiedStatus(isEmailVerified)}
                      </div>
                      <input type="text" className="eProfileSummaryInput" value={email || ''} disabled={true} />
                      <div className="upProfileHint">Your login ID.</div>
                    </div>

                    <div className="upProfileField">
                      <div className="upProfileLabelWithStatus">
                        <label className="upProfileLabel">Phone number</label>
                        {this.renderVerifiedStatus(isPhoneVerified)}
                      </div>
                      <input
                        type="text"
                        className="eProfileSummaryInput"
                        value={`${phoneCode || ''}${phoneBody ? ` ${phoneBody}` : ''}`.trim()}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Form>
        )}
      </Formik>
    );
  }
}
